import * as React from 'react';
import * as qs from 'qs';
import * as moment from 'moment';
import {
    GroupChart,
    PVACharacteristicGroupModal,
    PVAStyle,
    PVANav,
    PVASelect,
    LoadingIndicator,
    PVAContainerSimple,
    PVAContentHeader,
    PVAContentBody,
    PVABadge,
    PonderaFooter
} from '../../../components';
import { ReportType, MidRangeType } from '../../../enums';
import { ReportDataService, StyleDataService, UserDataService } from '../../../services';
import { Style, StyleText, SurveyNote } from '../../../models';
import { BaseRouteProps } from '../../../interfaces';
import { UserStore, FilterStore } from '../../../stores';
import { computed } from 'mobx';
import { observer, inject } from 'mobx-react';
import { PVAStyleContent } from '../../inclinationmodals/PVAStyleContent';

import './IndividualReportBlock.css';

interface IndividualReportState {
    selectedUserData: any;
    reportType: ReportType;
    midRangeType: MidRangeType;
    sticky: boolean;
    stickySize: number;
    stickyGroups: number[];
    groupPos: number[];
    activeInd: number;
    orgId: number;
    userId?: number;
    surveyVersionId: number;
    loading: boolean;
    pdfLoader: boolean;
    open: boolean;
    notes: SurveyNote[];
    workingNote: string;
    showNotes: boolean;
    private: boolean;
    surveyId?: number;
    retainUserId: boolean;
    linkedFrom9Box: boolean;
    small: boolean;
    allStyles?: Style[];
}

interface IndividualReportBlockProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
    showDropDown: boolean;
}

@inject('UserStore', 'FilterStore') @observer
export class IndividualReportBlock extends React.Component<IndividualReportBlockProps, IndividualReportState> {
    private userDataService = new UserDataService(this.props.notificationService);
    private reportDataService = new ReportDataService(this.props.notificationService);
    private banner: any;
    private pageTop: any;
    private groupEls: any[];
    private styleDataService = new StyleDataService(this.props.notificationService);

    @computed get userId() {
        return this.props.UserStore!.userInfo.id;
    }

    @computed get userRole() {
        return this.props.UserStore!.userInfo.userRole;
    }

    @computed get userPermissions() {
        return this.props.UserStore!.userInfo.userPermissions;
    }

    @computed get hasLimitedReport() {
        return this.props.UserStore!.checkUserPermission('limitedReport');
    }

    @computed get userCanSeeRIandRB() {
        return this.props.UserStore!.checkUserPermission('viewResponseIndicatorsAndBreakdown')
            || this.props.UserStore!.userRoleIn(['superAdmin', 'siteAdmin', 'ponderaAssociate']);
    }

    @computed get survey() {
        if (this.props.FilterStore && this.props.FilterStore.survey) {
            return this.props.FilterStore.survey;
        }
        return null;
    }

    @computed get surveyVersion() {
        if (this.props.FilterStore && this.props.FilterStore.surveyVersion) {
            return this.props.FilterStore.surveyVersion;
        }
        return null;
    }

    @computed get noSurveys() {
        return this.props.FilterStore && this.props.FilterStore.noSurveys;
    }

    constructor(props: IndividualReportBlockProps) {
        super(props);

        this.groupEls = [];

        const userId = qs.parse(this.props.location.search.replace('?', '')).uid;

        this.state = {
            selectedUserData: null,
            reportType: ReportType.Standard,
            midRangeType: MidRangeType.Default,
            sticky: false,
            stickySize: 0,
            stickyGroups: [],
            groupPos: [],
            activeInd: -1,
            orgId: 0,
            surveyId: 0,
            surveyVersionId: 0,
            userId: userId,
            loading: true,
            pdfLoader: false,
            open: false,
            notes: [],
            workingNote: '',
            showNotes: false,
            private: true,
            retainUserId: userId ? true : false,
            linkedFrom9Box: userId ? true : false,
            small: false,
            allStyles: [],
        };
    }

    private updateReport() {
        var userId = this.state.userId;

        if (!this.state.userId && this.props.FilterStore!.individualReportUserId) {
            userId = this.props.FilterStore!.individualReportUserId;
            this.setState({
                userId: userId
            });

            if (this.props.FilterStore!.individualReportUserData) {
                var storedReportData = this.props.FilterStore!.individualReportUserData;
                var storedStickyGroups: number[] = [];
                var storedGroupPos: number[] = [];
                storedReportData.inclinationGroups.map((data: any, ind: number) => {
                    storedStickyGroups[ind] = 0;
                    storedGroupPos[ind] = 0;
                });
                this.setState({
                    selectedUserData: storedReportData,
                    stickyGroups: storedStickyGroups,
                    groupPos: storedGroupPos,
                    retainUserId: false, // once we get a data load we no longer need to retain the query string user id
                    loading: false
                });

                if (userId) {
                    this.userDataService.getNotes(userId).then((notes: SurveyNote[]) => {
                        this.setState({
                            notes: notes
                        });
                    });
                }

                return;
            }
        }

        if (this.survey && this.surveyVersion && userId) {
            this.setState({
                loading: true
            });

            this.reportDataService.getIndividualReportData(this.survey.id, userId || undefined, this.surveyVersion.id).then((reportData) => {
                this.props.FilterStore!.individualReportUserData = reportData;
                this.props.FilterStore!.individualReportUserId = userId;

                var stickyGroups: number[] = [];
                var groupPos: number[] = [];
                reportData.inclinationGroups.map((data: any, ind: number) => {
                    stickyGroups[ind] = 0;
                    groupPos[ind] = 0;
                });
                this.setState({
                    selectedUserData: reportData,
                    stickyGroups: stickyGroups,
                    groupPos: groupPos,
                    loading: false,
                    retainUserId: false, // once we get a data load we no longer need to retain the query string user id
                });

                if (userId) {
                    this.userDataService.getNotes(userId).then((notes: SurveyNote[]) => {
                        this.setState({
                            notes: notes
                        });
                    });
                }
            });
        } else if (this.survey && this.surveyVersion) {
            this.setState({
                loading: true
            });

            this.reportDataService.getIndividualReportStructureData(this.surveyVersion.id).then((reportData) => {
                var stickyGroups: number[] = [];
                var groupPos: number[] = [];
                reportData.inclinationGroups.map((data: any, ind: number) => {
                    stickyGroups[ind] = 0;
                    groupPos[ind] = 0;
                });
                this.setState({
                    selectedUserData: reportData,
                    stickyGroups: stickyGroups,
                    groupPos: groupPos,
                    loading: this.state.retainUserId ? true : false // retain id means we arrived at the page with a query string, so we don't want to see the blank report
                });
            });
        }
    }

    private updateOrganization() {
        if (this.organization !== null && this.state.orgId !== this.organization.id) {
            const orgId = this.organization.id;

            const userId = this.state.userId;
            this.setState(
                {
                    orgId: orgId,
                    notes: [],
                    selectedUserData: undefined,
                    userId: this.state.retainUserId ? userId : undefined
                }, 
                this.updateReport);

            return true;
        }
        return false;
    }

    private update() {
        if (!this.props.showDropDown) {
            // My Report
            if (this.survey && this.state.surveyId !== this.survey.id) {
                this.setState({
                    surveyId: this.survey.id,
                    loading: true
                });
    
                this.reportDataService.getIndividualReportData(this.survey.id).then((reportData) => {
                    var stickyGroups: number[] = [];
                    var groupPos: number[] = [];
                    reportData.inclinationGroups.map((data: any, ind: number) => {
                        stickyGroups[ind] = 0;
                        groupPos[ind] = 0;
                    });
                    this.setState({
                        selectedUserData: reportData,
                        stickyGroups: stickyGroups,
                        groupPos: groupPos,
                        loading: false,
                        retainUserId: false, // once we get a data load we no longer need to retain the query string user id
                    });
                });
            } else if (this.state.linkedFrom9Box && this.props.FilterStore!.individualReportUserId === undefined) {
                // Linked From 9 Box
                this.setState({
                    // surveyId: this.survey.id,
                    loading: true
                });
                
                this.props.FilterStore!.individualReportUserId = this.state.userId!;
                if (this.surveyVersion && !this.surveyVersion.published) {
                    // If the selected survey version is an unpublished version
                    // then load the individual report as if it were that version
                    // Only Super Admins will have access to this
                    if (this.state.userId) {
                        this.reportDataService.getIndividualReportData(this.state.surveyId, this.state.userId, this.surveyVersion.id).then((reportData: any) => { // this.survey.id, userId, surveyVersionId
                            this.props.FilterStore!.individualReportUserData = reportData;
                            this.props.FilterStore!.individualReportUserId = this.state.userId;
                            var stickyGroups: number[] = [];
                            var groupPos: number[] = [];
                            reportData.inclinationGroups.map((data: any, ind: number) => {
                                stickyGroups[ind] = 0;
                                groupPos[ind] = 0;
                            });
                            this.setState({
                                selectedUserData: reportData,
                                stickyGroups: stickyGroups,
                                groupPos: groupPos,
                                retainUserId: false, // once we get a data load we no longer need to retain the query string user id
                                loading: false
                            });

                            if (this.state.userId) {
                                this.userDataService.getNotes(this.state.userId).then((notes: SurveyNote[]) => {
                                    this.setState({
                                        notes: notes
                                    });
                                });
                            }
                        });
                    }
                } else {
                    this.reportDataService.getIndividualReportDataLatest(this.state.userId!).then((reportData) => {
                        this.props.FilterStore!. individualReportUserData = reportData;

                        var stickyGroups: number[] = [];
                        var groupPos: number[] = [];
                        reportData.inclinationGroups.map((data: any, ind: number) => {
                            stickyGroups[ind] = 0;
                            groupPos[ind] = 0;
                        });
                        this.setState({
                            selectedUserData: reportData,
                            stickyGroups: stickyGroups,
                            groupPos: groupPos,
                            loading: false,
                            retainUserId: false, // once we get a data load we no longer need to retain the query string user id
                        });
                    });
                }
            }
        } else {
            // Individual Report
            if (this.userRole !== 'user') {
                if (this.surveyVersion !== null && this.state.surveyVersionId !== this.surveyVersion.id) {
                    this.setState({
                        surveyVersionId: this.surveyVersion.id
                    });

                    if (!this.updateOrganization()) {
                        this.updateReport();
                    }
                } else {
                    this.updateOrganization();
                }
            }
        }
    }

    componentDidMount() {
        this.update();
        this.handleQueryStringLoad();
    }

    componentWillReact() {
        this.update();
    }

    handleQueryStringLoad() {
        const userId = this.state.userId;
        if (userId !== undefined) {
            // make a dummy event object
            const dummyEvent = {
                target: {
                    value: userId
                }
            };
            this.userChange(dummyEvent, true);
        }
    }

    updateNav() {
        var stick = this.state.stickySize;

        if (this.banner && this.state.stickySize === 0) {
            this.setState({
                stickySize: this.banner.offsetTop
            });
            stick = this.banner.offsetTop;
        }

        var sticky = stick;
        var markSticky = false;

        if (this.pageTop.scrollTop >= sticky) {
            markSticky = true;
        } else {
            markSticky = false;
        }

        var stickies = this.state.stickyGroups;
        var i: number = 0;
        var activeInd: number = -1;
        if (this.state.sticky) {
            for (; i < this.groupEls.length; i++) {
                if (this.state.groupPos[i] === 0 && this.groupEls[i]) {
                    this.state.groupPos[i] = this.groupEls[i].offsetTop;
                }
                stickies[i] = 0; // white
                if (this.state.groupPos[i] !== 0) {
                    if (this.pageTop.scrollTop >= this.state.groupPos[i]) {
                        stickies[i] = 1; // active
                        activeInd = i;
                        for (var t: any = 0; t < i; t++) {
                            stickies[t] = 2; // past
                        }
                    }
                }
            }
        } else {
            for (; i < this.groupEls.length; i++) {
                stickies[i] = 0;
            }
        }

        this.setState({
            stickyGroups: stickies,
            activeInd: activeInd,
            sticky: markSticky
        });
    }

    handleScroll(event: any, t: any) {
        if (event.target.getAttribute('class') !== 'site-content-inner') {
            return;
        }
        this.updateNav();
    }

    @computed get organizationName() {
        if (this.props.FilterStore && this.props.FilterStore.organization) {
            return `${this.props.FilterStore!.organization!.name}`;
        }
        return '';
    }
    
    @computed get organization() {
        if (this.props.FilterStore && this.props.FilterStore.organization) {
            return this.props.FilterStore.organization;
        }
        return null;
    }

    @computed get surveyReportType() {
        if (this.props.FilterStore && this.props.FilterStore.surveyReportType) {
            return this.props.FilterStore.surveyReportType;
        }
        return null;
    }

    @computed get users() {
        if (this.props.FilterStore && this.props.FilterStore.users) {
            return this.props.FilterStore.users;
        }
        return [];
    }

    @computed get teamID() {
        if (this.props.FilterStore && this.props.FilterStore.teamId) {
            return this.props.FilterStore.teamId;
        }
        return undefined;
    }

    public userChange(e: any, userIdFromQueryString: boolean = false) {
        var userId: number = Number(e.target.value);
        this.setState({
            userId: userId,
            loading: true,
            retainUserId: userIdFromQueryString
        });

        this.props.FilterStore!.individualReportUserId = userId;

        if (this.userRole !== 'user') {
            // var surveyVersionId: number = 0;
            // if (this.state.surveyVersionId !== 0) {
            //     surveyVersionId = this.state.surveyVersionId;
            // }

            if (this.survey && userId) {
                var surveyVersionID: number | undefined = undefined;
                if (this.surveyVersion && !this.surveyVersion.published) {
                    surveyVersionID = this.surveyVersion.id;
                }

                this.reportDataService.getIndividualReportData(this.survey.id, userId, surveyVersionID).then((reportData: any) => { // this.survey.id, userId, surveyVersionId
                    this.props.FilterStore!.individualReportUserData = reportData;
                    this.props.FilterStore!.individualReportUserId = userId;
                    var stickyGroups: number[] = [];
                    var groupPos: number[] = [];
                    reportData.inclinationGroups.map((data: any, ind: number) => {
                        stickyGroups[ind] = 0;
                        groupPos[ind] = 0;
                    });
                    this.setState({
                        selectedUserData: reportData,
                        stickyGroups: stickyGroups,
                        groupPos: groupPos,
                        retainUserId: false, // once we get a data load we no longer need to retain the query string user id
                        loading: false
                    });

                    if (userId) {
                        this.userDataService.getNotes(userId).then((notes: SurveyNote[]) => {
                            this.setState({
                                notes: notes
                            });
                        });
                    }
                });
            } else if (this.surveyVersion) {
                this.props.FilterStore!.individualReportUserData = undefined;
                this.props.FilterStore!.individualReportUserId = undefined;
                this.reportDataService.getIndividualReportStructureData(this.surveyVersion.id).then((reportData) => {
                    var stickyGroups: number[] = [];
                    var groupPos: number[] = [];
                    reportData.inclinationGroups.map((data: any, ind: number) => {
                        stickyGroups[ind] = 0;
                        groupPos[ind] = 0;
                    });
                    this.setState({
                        selectedUserData: reportData,
                        stickyGroups: stickyGroups,
                        groupPos: groupPos,
                        loading: false,
                        notes: []
                    });
                });
            }
        }
    }
    
    private GetTeamID() {
        var result = this.teamID;
        if (result && result > 0) {
            return result;
        }
        if (this.state.selectedUserData) {
            return this.state.selectedUserData.teamID;
        }
        return undefined;
    }

    private loadSurveyInstance(id: number) {
        this.setState(
            {
                loading: true,
                open: !this.state.open
            }, 
            () => {
                var surveyVersionID: number | undefined = undefined;
                if (this.surveyVersion && !this.surveyVersion.published) {
                    surveyVersionID = this.surveyVersion.id;
                }
                this.reportDataService.getIndividualReportDataByInstance(id, surveyVersionID).then((reportData: any) => { // this.survey.id, userId, surveyVersionId
                    this.props.FilterStore!.individualReportUserData = reportData;
                    this.props.FilterStore!.individualReportUserId = this.state.userId;
                    var stickyGroups: number[] = [];
                    var groupPos: number[] = [];
                    reportData.inclinationGroups.map((data: any, ind: number) => {
                        stickyGroups[ind] = 0;
                        groupPos[ind] = 0;
                    });
                    this.setState({
                                      selectedUserData: reportData,
                                      stickyGroups: stickyGroups,
                                      groupPos: groupPos,
                                      retainUserId: false, // once we get a data load we no longer need to retain the query string user id
                                      loading: false
                                  });
                });
            });
    }

    exportPDF = () => {
        if (this.PdfExportEnabled()) {
            this.setState(
            {
                pdfLoader: true,
                open: false
            });
            this.reportDataService.getIndividualReportDataPDF(this.state.selectedUserData.surveyInstanceId, 'MyReport', this.surveyReportType ? this.surveyReportType.id : undefined).then((reportData: any) => {
                this.setState(
                    {
                        pdfLoader: false
                    });
                })
                .catch((error) => {
                    console.log(error);
                    this.setState(
                        {
                            pdfLoader: false
                        });
                });
        }
    }

    private exportBriefPDF = () => {
        if (this.PdfExportEnabled()) {
            this.setState(
                {
                    pdfLoader: true,
                    open: false
                }
            );
            this.reportDataService.getIndividualBriefPDF(this.state.selectedUserData.surveyInstanceId, 'MyReport', this.surveyReportType ? this.surveyReportType.id : undefined).then((reportData: any) => {
                this.setState(
                    {
                        pdfLoader: false
                    }
                );
            })
            .catch((error) => {
                console.log(error);
                this.setState(
                    {
                        pdfLoader: false
                    }
                );
            });
        }
    }

    private exportLimitedStylePDF() {
        this.setState(
            {
                pdfLoader: true,
                open: false
            },
            () => {
                this.reportDataService.getLimitedStylePDF(this.getCurrentStyle().id, this.getCurrentStyle().name).then(() => {
                    this.setState({
                                      pdfLoader: false
                                  });
                }).catch((error) => {
                    console.log(error);
                    this.setState({
                                      pdfLoader: false
                                  });
                });
            }
        );
    }

    private exportNewUserGuidePDF() {
        this.setState(
            {
                pdfLoader: true,
                open: false
            },
            () => {
                this.reportDataService.getNewUserGuidePDF("PVA Style Report User’s Guide").then(() => {
                    this.setState({
                                      pdfLoader: false
                                  });
                }).catch((error) => {
                    console.log(error);
                    this.setState({
                                      pdfLoader: false
                                  });
                });
            }
        );
    }

    private HasAccessToMidrange() {
        return this.HasAccessToViewMidranges() && this.userPermissions.indexOf('canViewMidRangeChoices') > -1;
    }
    private HasAccessToViewMidranges() {
        return (this.userRole === 'siteAdmin' || this.userRole === 'superAdmin' || this.userPermissions.indexOf('viewMidRanges') > -1);
    }

    PdfExportEnabled = () => {
        let enabled = this.hasLimitedReport || !this.props.showDropDown || !!this.props.FilterStore!.individualReportUserId;
        return enabled;
    }

    private ActionsButton(sticky: boolean) {
        let pdfExportClasses = 'options-entry';
        if (!this.PdfExportEnabled()) {
            pdfExportClasses += ' disabled';
        }

        return (
            <div className="btn-group dropdown">
                <button type="button" className="btn btn-primary"  onClick={(e) => this.setState({open: !this.state.open })}>
                    <span>Actions </span>
                    <span className="caret" />
                </button>
                <ul className={`action-menu dropdown-menu ${(this.state.open && (((sticky && this.state.sticky)) || (!sticky && !this.state.sticky))) ? 'open' : ''}`}>
                    {this.state.reportType === ReportType.Midrange && this.HasAccessToMidrange() && <li className="options-header">Mid Range Options</li>}
                    {this.state.reportType === ReportType.Midrange && this.HasAccessToMidrange() && 
                        <li className="options-entry">      
                            <div className="options-entry-title">
                                <span className="glyphicon glyphicon-list-alt" /> <span> Use Mid Ranges From</span>
                            </div>              
                            <select className="form-control" onChange={(e) => { this.setState({midRangeType: parseInt(e.target.value, 10), open: !this.state.open}, this.forceUpdate); }}>
                                <option selected={this.state.midRangeType === MidRangeType.Default} value={MidRangeType.Default}>PVA</option>
                                <option selected={this.state.midRangeType === MidRangeType.Team} value={MidRangeType.Team}>Current Team</option>
                                <option selected={this.state.midRangeType === MidRangeType.Organization} value={MidRangeType.Organization}>Current Organization</option>
                                { this.props.UserStore!.userRoleIn(['superAdmin']) &&
                                    <option selected={this.state.midRangeType === MidRangeType.All} value={MidRangeType.All}>All PVA Data</option>
                                }
                            </select>
                        </li>
                    }
                    {this.HasAccessToViewMidranges() && <React.Fragment>
                        <li className="options-header">Change View</li>
                        {(this.state.reportType !== ReportType.Standard || this.state.showNotes) && 
                            <li className="options-entry" onClick={(e) => this.setState({reportType: ReportType.Standard, showNotes: false, open: false})}>
                                <div className="options-entry-title">
                                    <img src="/images/standardIcon.svg" />
                                    <span> Standard View</span>
                                </div>
                                <div className="options-entry-desc">
                                    View standard individual report
                                </div>
                            </li>
                        }
                        {(this.state.reportType !== ReportType.Midrange || this.state.showNotes) && 
                            <li className="options-entry" onClick={(e) => this.setState({reportType: ReportType.Midrange, showNotes: false, open: false})}>
                                <div className="options-entry-title">
                                    <img src="/images/midrangeIcon.svg" />
                                    <span> Mid Range View</span>
                                </div>
                                <div className="options-entry-desc">
                                View the individual report with your scores as they relate to the mid-range of 1.25 standard deviation below and above the mean for each scale
                                </div>
                            </li>
                        }
                    </React.Fragment>}
                    {!this.hasLimitedReport && !this.state.small && <li className="options-header">Report Size</li> &&
                    <li className="options-entry" onClick={(e) => this.setState({small: true, open: false})}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-search" /> <span> View Small Report</span>
                        </div>
                        <div className="options-entry-desc">
                            Show the report as a smaller version to better fit small screens
                        </div>
                    </li>}
                    {!this.hasLimitedReport && this.state.small && <li className="options-entry" onClick={(e) => this.setState({small: false, open: false})}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-search" /> <span> View Normal Report</span>
                        </div>
                        <div className="options-entry-desc">
                            Show the report at its normal size
                        </div>
                    </li>}
                    <li className="options-header">Export Options</li>
                    {this.hasLimitedReport && <li className={pdfExportClasses} onClick={(e) => this.exportLimitedStylePDF()}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-save-file" /> <span> My PVA Style Report</span>
                        </div>
                        <div className="options-entry-desc">
                            My PVA Style Report in PDF Format
                        </div>
                    </li>}
                    {this.hasLimitedReport && <li className={pdfExportClasses} onClick={(e) => this.exportNewUserGuidePDF()}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-save-file" /> <span> PVA Style Report User's Guide</span>
                        </div>
                        <div className="options-entry-desc">
                            PVA Style Report User's Guide in PDF Format
                        </div>
                    </li>}
                    {!this.hasLimitedReport && <li className={pdfExportClasses} onClick={(e) => this.exportBriefPDF()}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-save-file" /> <span> Export PDF Brief Report</span>
                        </div>
                        <div className="options-entry-desc">
                            PDF Export
                        </div>
                    </li>}
                    {!this.hasLimitedReport && <li className={pdfExportClasses} onClick={(e) => this.exportPDF()}>
                        <div className="options-entry-title">
                            <span className="glyphicon glyphicon-save-file" /> <span> Export Full-Detail PDF Report</span>
                        </div>
                        <div className="options-entry-desc">
                            PDF Export
                        </div>
                    </li>}
                    {this.state.selectedUserData && this.state.selectedUserData.others && this.state.selectedUserData.others.length > 0 && 
                        <div>
                            <li className="options-header">Past Surveys</li>
                            {this.state.selectedUserData.others.map((otherData: any, otherInd: number) => {
                                return <li key={`other-${otherInd}`} className="options-entry" onClick={(e) => this.loadSurveyInstance(otherData.id)}>
                                    <div className="options-entry-title">
                                        <span className="glyphicon glyphicon-save-file" /> <span> {otherData.dateTime}</span>
                                    </div>
                                </li>;
                            })}
                        </div>
                    }
                    {(this.userRole === 'siteAdmin' || this.userRole === 'superAdmin' || this.userPermissions.indexOf('viewAssessmentNotes') > -1) && !this.state.showNotes && 
                        <li className="options-entry" onClick={(e) => this.setState({showNotes: true, open: false})}>
                            <div className="options-entry-title">
                                <span className="glyphicon glyphicon-edit" /> <span> Notes</span>
                            </div>
                        </li>
                    }
                </ul>
        </div>);
    }

    private GetKeyOverride(key?: string, fallback?: any) {
        if (key && this.props.FilterStore) {
            return this.props.FilterStore.GetKeyOverride(key, fallback || '');
        }
        return fallback;
    }

    private getUserStyle() {
        if (this.state.selectedUserData && this.state.selectedUserData.style) {
            return this.state.selectedUserData.style;
        }
        return '';
    }

    getCurrentStyle() {
        let relevantStyle = this.getUserStyle();
        if (this.state.allStyles!.length > 0) {
            relevantStyle = this.state.allStyles!.filter((style: Style) => {
                return style.name === this.getUserStyle().name;
            })[0];
        }
        return relevantStyle;
    }

    content(type: string) {
        var result = this.getCurrentStyle().styleTexts.find((st: StyleText) => {
            return st.styleTextType === type;
        });
        if (result) {
            return result.value;
        }
        return '';
    }

    public render() {
        let userStyle = this.getCurrentStyle();
        let limitedReport = this.hasLimitedReport;

        let defaultOptions = [{
            id: 0,
            value: '- Clear Selected -'
        }];
        let contentHeaderSmall = (
            <div className="row">
                <div className="col-sm-6 col-md-8 col-xs-12">
                    {!this.props.showDropDown && <div className="title">
                        {this.GetKeyOverride('myReport', <span>My PVA Report <span className="trademark">TM</span></span>)} <span className="light">for </span>
                        <div className="select-box">
                            {this.state.selectedUserData && <span>{this.state.selectedUserData.name}</span>}
                            {this.state.selectedUserData && this.state.selectedUserData.style && <PVABadge style={this.state.selectedUserData.style.name} badge={this.state.selectedUserData.style.name[0]} />}
                        </div>
                    </div>}
                    {this.props.showDropDown && <div className="title">
                        {this.GetKeyOverride('individualReport', <span>PVA Individual Report <span className="trademark">TM</span></span>)} <span className="light">for </span>
                        <PVASelect defaultOptions={defaultOptions} options={this.users} selected={this.state.userId} onChange={(e: any) => this.userChange(e)} />
                    </div>}
                </div>
                <div className="col-sm-6 col-md-4 text-right">
                    {this.ActionsButton(true)}
                </div>
            </div>
        );

        if (limitedReport) {
            if (this.state.allStyles!.length < 1) {
                this.styleDataService.getAllBySurveyVersion(this.surveyVersion!).then((styles: Style[]) => {
                    this.setState(() => { return { allStyles: styles }; } );
                });
            }

            if (!userStyle || this.state.allStyles!.length < 1) {
                return (<LoadingIndicator />);
            }

            const sortedAdditionalStyles = userStyle.styleTexts.filter((styleText: StyleText) => {
                return styleText.styleTextType === 'additional';
            }).sort((one: StyleText, two: StyleText) => {
                if (one.sort > two.sort) {
                    return 1;
                } else if (one.sort < two.sort) {
                    return -1;
                }
                return 0;
            });

            return (
                <div>
                    <div className='content-header'>
                        <div className='content-container'>
                            <div className='col-sm-10 col-md-8 col-xs-12'>
                                <div className='title'>
                                    {this.GetKeyOverride('myReport', <span>My PVA Report <span className="trademark">TM</span></span>)} <span className="light">for </span>
                                    <div className="select-box">
                                        {this.state.selectedUserData && <span>{this.state.selectedUserData.name}</span>}
                                        {this.state.selectedUserData && this.state.selectedUserData.style && <PVABadge style={this.state.selectedUserData.style.name} badge={this.state.selectedUserData.style.name[0]} />}
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-sm-6 col-md-4 text-right">
                            {this.ActionsButton(false)}
                        </div>
                    </div>
                    <div className='content-body'>
                        <PVAStyleContent title={'title goes here'} asModal={false} notificationService={this.props.notificationService} style={this.state.selectedUserData.style} name={userStyle.name} exporting={false} sortedAdditionalStyles={sortedAdditionalStyles} />
                    </div>

                    <PonderaFooter />
                </div>
            );
        }

        let riAndRbComponent = null;
        if (this.userCanSeeRIandRB) {
            const rbContent = this.buildResponseBreakdownContent();
            var offset = 0;

            riAndRbComponent = (
                <div className="row">
                    {
                        this.state.selectedUserData && this.state.selectedUserData.inclinationGroups.filter(this.filterResponseIndicators.bind(this)).map((groupData: any, index: number) => {
                            var riContent = this.buildResponseIndicator(groupData, offset);
                            offset += groupData.data.length;
                            return <div className="col-xs-12 col-sm-6" key={`ri-${index}`}>
                                <PVAContainerSimple title={groupData.name} content={riContent} defaultOpen={false} />
                            </div>;
                        })
                    }
                    <div className="col-xs-12 col-sm-6">
                        <PVAContainerSimple title={'Response Breakdown'} content={rbContent} defaultOpen={false} />
                    </div>
                </div>
            );
        }

        var resultGroups = <div />;
        if (!this.state.loading && this.state.selectedUserData) {
            var indexOffset = 0;
            resultGroups = (
                <div>
                    {this.state.selectedUserData.inclinationGroups.filter(this.filterNormalIndicators.bind(this)).map((groupData: any, index: any) => {
                        var chart = (<div ref={(e) => this.groupEls[index] = e}>
                            <GroupChart teamID={this.GetTeamID()} style={userStyle} groupData={groupData} reportType={this.state.reportType} midRangeType={this.state.midRangeType} index={index} offset={indexOffset} headerOffset={true} small={this.state.small} allowToggle={this.userPermissions.indexOf('scoreBarColorChange') > -1}/>
                            </div>);
                        indexOffset += groupData.data.length;
                        if (groupData.subGroups) {
                            groupData.subGroups.map((sg: any) => {
                                indexOffset += sg.data.length;
                            });
                        }

                        return <div key={`chart-${index}`}>
                            {chart}
                            </div>;
                    })}
                </div>);
        }

        const notesTable = (
        <table className="table table-striped">
            <thead>
                <tr>
                    <th className="table-column-thin">Created</th>
                    <th className="table-column-thin">Added By</th>
                    <th className="table-column-wide">Note</th>
                    <th className="table-column-thin" />
                </tr>
            </thead>
            <tbody className="vertical-align-cells">
                {this.state.notes.map((note: SurveyNote, ind: number) => {
                    return <tr key={`note-${note.id}`}>
                        <td>{moment(note.created).format('L LT')}</td>
                        <td>{note.createdByLastName}, {note.createdByFirstName}</td>
                        <td>{note.value}</td>
                        <td>
                            { this.userId === note.createdById &&
                                <button className="btn btn-danger" onClick={() => this.deleteNote(note.id)}>Remove Note</button>
                            }
                        </td>
                    </tr>;
                })}
            </tbody>
        </table>);

        const noteForm = (
            <div>
                <div className="row">
                    <div className="col-xs-11">
                        <label>Note</label>
                        <textarea className="form-control" onChange={(e) => this.setState({ workingNote: e.target.value })} value={this.state.workingNote} />
                    </div>
                    <div className="col-xs-1">
                        <label>Private</label>
                        <input type="checkbox" className="form-control" checked={this.state.private} onChange={(e) => this.setState({private: !this.state.private})} />
                    </div>
                </div>

                <br />
                <button className="btn btn-primary" disabled={this.state.workingNote.length === 0} onClick={(e) => this.createNote()}>Add</button>
            </div>
        );

        return (
            <div className="site-content-inner" ref={(e) => this.pageTop = e} onScroll={(e) => this.handleScroll(e, this)}>
                {this.noSurveys && <div id={`noSurveys-${this.noSurveys}`} />}
                {this.organization && <div id={`organization-${this.organization.id}`} />}
                {this.survey && <div id={`survey-${this.survey.id}`} />}
                {this.surveyVersion && <div id={`surveyVersion-${this.surveyVersion.id}`} />}
                {this.surveyReportType && <div id={`surveyReportType-${this.surveyReportType.id}`} />}
                
                <PVAContentHeader>
                    <div className="row">
                        <div className="col-sm-10 col-md-8 col-xs-12">
                            {!this.props.showDropDown && <div className="title">
                                {this.GetKeyOverride('myReport', <span className="hidden-800"> My PVA Report <span className="trademark">TM</span></span>)} <span className="light hidden-800">for </span>
                                <div className="select-box">
                                    {this.state.selectedUserData && <span>{this.state.selectedUserData.name}</span>}
                                    {this.state.selectedUserData && this.state.selectedUserData.style && <PVABadge style={this.state.selectedUserData.style.name} badge={this.state.selectedUserData.style.name[0]} />}
                                </div>
                            </div>}
                                {this.props.showDropDown && <div className="title">
                                {this.GetKeyOverride('individualReport', <span>PVA Individual Report <span className="trademark">TM</span></span>)} <span className="light">for </span>
                                {!this.state.sticky && <PVASelect defaultOptions={defaultOptions} options={this.users} selected={this.state.userId} onChange={(e: any) => this.userChange(e)} />}
                            </div>}
                        </div>
                        <div className="col-sm-2 col-md-4 text-right">
                            {this.ActionsButton(false)}
                        </div>
                    </div>
                </PVAContentHeader>
                
                <div ref={(e) => this.banner = e}>
                    {this.state.selectedUserData && <PVANav FilterStore={this.props.FilterStore} content={contentHeaderSmall} style={userStyle} activeInd={this.state.activeInd} groups={this.state.selectedUserData.inclinationGroups} stickyGroups={this.state.stickyGroups} sticky={this.state.sticky} onSelect={(e: any) => this.updateNav()} />}
                </div>

                <PVAContentBody>
                    <div className={this.state.small ? 'scaled-report-small' : ''}>
                        {this.state.sticky && <div className="sticky-buffer" />}
                        {this.state.loading && <LoadingIndicator />}
                        {this.state.pdfLoader && <LoadingIndicator showPdfDescription={true} showInPanel={true} showBackground={true}/>}

                        {!this.state.loading && !this.state.pdfLoader && this.state.selectedUserData && this.state.selectedUserData.style && <PVAStyle notificationService={this.props.notificationService} name={this.state.selectedUserData.name || ' '} style={this.state.selectedUserData.style} brief={this.state.selectedUserData.style.brief} content={this.state.selectedUserData.style.content} core={this.state.selectedUserData.style.core} />}
                        {!this.state.loading && !this.state.pdfLoader && this.state.selectedUserData && !this.state.showNotes &&
                            <div>
                                {resultGroups}
                                {riAndRbComponent}
                            </div>
                        }
                    </div>

                    {(this.userRole === 'siteAdmin' || this.userRole === 'superAdmin' || this.userPermissions.indexOf('viewAssessmentNotes') > -1) && this.state.showNotes && this.state.selectedUserData && 
                        <div>
                            <PVAContainerSimple title={'Notes'} content={notesTable} defaultOpen={true} />
                            <PVAContainerSimple title={'Add New Note'} content={noteForm} defaultOpen={false} />
                        </div>
                    }

                    <div className="text-right" style={{color: '#ccc'}}>
                        <div>
                            {this.state.selectedUserData && this.state.selectedUserData.surveyVersionName}                            
                            {this.state.selectedUserData && this.state.selectedUserData.styleOverridden && <span> <small>*</small></span>}
                        </div>
                        <div>{this.getDateString()}</div>
                    </div>
                </PVAContentBody>
            </div>);
    }

    private filterResponseIndicators(x: any) {
        if (x.displayType !== 'responseIndicator') {
            return false;
        }
        if (!this.surveyReportType) {
            return true;
        }
        return x.surveyReportTypeID === this.surveyReportType.id;
    }

    private filterNormalIndicators(x: any) {
        if (x.displayType !== 'normal') {
            return false;
        }
        if (!this.surveyReportType) {
            return true;
        }
        return x.surveyReportTypeID === this.surveyReportType.id;
    }

    getDateString() {
        if (!this.state.selectedUserData || !this.state.selectedUserData.completed) {
            return '';
        }

        var d = new Date(this.state.selectedUserData.completed);
        return (d.getMonth() + 1) + '/' + d.getDate() + '/' + d.getFullYear() + ' ' + d.getHours() + ':' + d.getMinutes();
    }

    deleteNote(noteId: number) {
        const reallyDelete = confirm('Are you sure you want to delete this note?');

        if (reallyDelete) {
            this.userDataService.deleteNote(noteId)
                .then(() => {
                    this.props.notificationService.addNotification('Note removed successfully.');

                    this.setState({notes: this.state.notes.filter((x) => x.id !== noteId)});
                })
                .catch(() => this.props.notificationService.addError('Error removing note, please try again.'));
        }
    }

    createNote() {
        var note = new SurveyNote();
        note.value = this.state.workingNote;
        note.private = this.state.private;
        this.userDataService.createNote(this.state.selectedUserData.surveyInstanceId, note)
            .then((savedNote: SurveyNote) => {
                this.props.notificationService.addNotification('Note successfully added.');
                var notes = this.state.notes;
                notes.push(savedNote);
                this.setState({
                    notes: notes,
                    workingNote: ''
                });
            })
            .catch(() => this.props.notificationService.addError('Error adding note, please try again.'));
    }

    toggleModal(index: number) {
        let { selectedUserData } = this.state;

        var offset = 0;
        selectedUserData.inclinationGroups.filter(this.filterResponseIndicators.bind(this)).map((groupData: any) => {
            var data = groupData.data.sort(this.basicSort);
            data.map((c: any, cInd: number) => {
                if (offset + cInd === index) {
                    c.showModal = true;
                } else {
                    c.showModal = false;
                }
            });
            offset += data.length;
        });

        this.setState(
            { 
                selectedUserData: selectedUserData 
            },
            this.forceUpdate);
    }

    private basicSort(a: any, b: any) {
        if (a.sort > b.sort) {
            return 1;
        }
        if (a.sort < b.sort) {
            return -1;
        }
        return 0;
    }

    private buildResponseIndicator(groupData: any, off: number) {
        var data = groupData.data.sort(this.basicSort);

        return (
            <div>
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <td />
                            <td>Indicator</td>
                            <td className="text-right">Score</td>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((c: any, cIndex: number) => {
                            var offset = off + cIndex;
                            return <tr key={`group-${groupData.characteristicGroupID}-index-${cIndex}`}>
                                <td>
                                    <span onClick={(e) => this.toggleModal(offset)} className="characteristic-info glyphicon glyphicon-info-sign icon-button" />
                                </td>
                                <td>
                                    <span onClick={(e) => this.toggleModal(offset)}>{c.name}</span>
                                </td>
                                <td className="text-right">
                                    <span>{c.value.toFixed(0)}%</span>
                                </td>
                            </tr>;
                        })}
                    </tbody>
                </table>
                {
                    data.map((c: any, cIndex: number) => {
                        const props = {
                            show: c.showModal,
                            onHide: this.toggleModal.bind(this, -1),
                            title: c.name,
                            core: ' ',
                            content: c.helpText,
                            step: 0,
                            style: this.state.selectedUserData.style
                        };

                        return (
                            <div key={`ri-modal-${off + cIndex}`}>
                                <PVACharacteristicGroupModal {...props} />
                            </div>);
                    })
                }
            </div>
        );
    }

    private buildResponseBreakdownContent() {
        const { selectedUserData } = this.state;

        if (selectedUserData && selectedUserData.responseBreakdowns) {
            const totalCount = selectedUserData.responseBreakdowns.reduce((prev: any, curr: any) => prev + curr.count, 0);
            const totalPercentage = selectedUserData.responseBreakdowns.reduce((prev: any, curr: any) => prev + curr.percentage, 0);

            return (
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <td>Response</td>
                            <td className="text-right">Count</td>
                            <td className="text-right">Percentage</td>
                        </tr>
                    </thead>
                    <tbody>
                        {selectedUserData.responseBreakdowns.map((rb: any) => {
                            return <tr key={`index-${rb.value}`}>
                                <td>{rb.value}</td>
                                <td className="text-right">{rb.count}</td>
                                <td className="text-right">{rb.percentage.toFixed(1)}%</td>
                            </tr>;
                        })}
                        <tr>
                            <td>Total</td>
                            <td className="text-right">{totalCount}</td>
                            <td className="text-right">{totalPercentage.toFixed(1)}%</td>
                        </tr>
                    </tbody>
                </table>);
        }

        return null;
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/blocks/IndividualReportBlock.tsx